import ModalV2 from '@/components/ModalV2';
import Select from '@/components/Select';
import Tag from '@/components/Tag';
import useUser from '@/hooks/useUser';
import { PROJECT_HEALTH_OPTIONS, PROJECT_HEALTH_COLORS } from '@/screens/monitoring/lib/gridConstants';
import projectActions from '@/screens/workflows/redux/actions/projectActions';
import { useCallback, useMemo, useState } from 'react';

const healthDot = (color, size = 8) => <span className="health-dot" style={{ width: size, height: size, backgroundColor: color }} />;

const ProjectHealthModal = ({ show, onHide, project, integrators = [] }) => {
  const user = useUser();

  const [selectedHealth, setSelectedHealth] = useState(null);
  const [date, setDate] = useState('');
  const [statusNote, setStatusNote] = useState('');
  const [actionNote, setActionNote] = useState('');
  const [followUpPersonId, setFollowUpPersonId] = useState('');
  const [saving, setSaving] = useState(false);

  const currentHealth = selectedHealth ?? project?.projectHealth ?? null;
  const currentOption = PROJECT_HEALTH_OPTIONS.find((o) => o.value === currentHealth);
  const currentColor = PROJECT_HEALTH_COLORS[currentHealth];

  const existingNotes = useMemo(() => {
    const notes = project?.projectHealthNotes;

    if (!Array.isArray(notes)) {
      return [];
    }

    return notes;
  }, [project?.projectHealthNotes]);

  const resetForm = useCallback(() => {
    setSelectedHealth(null);
    setDate('');
    setStatusNote('');
    setActionNote('');
    setFollowUpPersonId('');
    setSaving(false);
  }, []);

  const handleHide = useCallback(() => {
    resetForm();
    onHide();
  }, [onHide, resetForm]);

  const handleHealthChange = useCallback((option) => {
    setSelectedHealth(typeof option === 'string' ? option : option?.value);
  }, []);

  const handleSave = useCallback(async () => {
    if (!project?.id || !currentHealth) {
      return;
    }

    setSaving(true);

    const followUpPerson = integrators.find((i) => i.value === followUpPersonId);
    const noteDate = date || new Date().toISOString().split('T')[0];

    const newNote = {
      date: noteDate,
      authorId: user.jwt.id,
      authorName: user.jwt.name,
      health: currentHealth,
      statusNote: statusNote.trim(),
      actionNote: actionNote.trim(),
      followUpPersonId: followUpPersonId || null,
      followUpPersonName: followUpPerson?.label || null,
      createdAt: new Date().toISOString()
    };

    const healthChanged = currentHealth !== (project?.projectHealth ?? null);
    const hasNoteContent = newNote.statusNote || newNote.actionNote || newNote.followUpPersonId;
    const shouldRecordNote = hasNoteContent || healthChanged;
    const updatedNotes = shouldRecordNote ? [newNote, ...existingNotes] : existingNotes;

    await projectActions.set('projectHealth', currentHealth, { projectId: project.id });

    if (shouldRecordNote) {
      await projectActions.set('projectHealthNotes', updatedNotes, { projectId: project.id });
    }

    handleHide();
  }, [project?.id, currentHealth, date, statusNote, actionNote, followUpPersonId, user, integrators, existingNotes, handleHide]);

  const todayStr = new Date().toISOString().split('T')[0];

  const actions = useMemo(
    () => [
      {
        children: 'Save',
        variant: 'primary',
        onClick: handleSave,
        disabled: !currentHealth || saving
      }
    ],
    [handleSave, currentHealth, saving]
  );

  return (
    <ModalV2 show={show} onHide={handleHide} title="Project Health" description={project?.name || ''} maxWidth="880px" maxHeight="85vh" actions={actions} className="project-health-modal" portal>
      <div className="modal-content">
        <div className="left-column">
          <div className="health-picker">
            <div className="field-label">Health Status</div>
            <div className="health-tag-wrapper">
              {currentColor && healthDot(currentColor, 10)}
              <Tag
                options={PROJECT_HEALTH_OPTIONS}
                value={currentHealth}
                onChange={handleHealthChange}
                useSearch={false}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: currentColor || '#899ba5',
                  cursor: 'pointer',
                  fontSize: 13,
                  fontWeight: 500
                }}>
                {currentOption?.label || 'Select health status'}
              </Tag>
            </div>
          </div>

          <div className="note-form">
            <div className="form-title">Leave a Note</div>
            <div className="form-fields">
              <div className="form-row">
                <div className="form-group">
                  <label className="field-label">Date</label>
                  <input type="date" value={date || todayStr} onChange={(e) => setDate(e.target.value)} className="form-input" />
                </div>
                <div className="form-group">
                  <label className="field-label">Who is leaving this note</label>
                  <input type="text" value={user.jwt.name || ''} readOnly className="form-input readonly" />
                </div>
              </div>
              <div>
                <label className="field-label">Note on Status</label>
                <textarea value={statusNote} onChange={(e) => setStatusNote(e.target.value)} placeholder="Why is the project in this status?" rows={2} className="form-textarea" />
              </div>
              <div>
                <label className="field-label">What is being done to handle</label>
                <textarea value={actionNote} onChange={(e) => setActionNote(e.target.value)} placeholder="Actions being taken or next steps" rows={2} className="form-textarea" />
              </div>
              <div>
                <label className="field-label">Who is following up</label>
                <Select value={integrators.find((i) => i.value === followUpPersonId) || followUpPersonId} options={integrators} onChange={(option) => setFollowUpPersonId(option?.value || '')} placeholder="Select a person" nullable />
              </div>
            </div>
          </div>
        </div>

        <div className="right-column">
          <div className="history-title">History</div>
          {existingNotes.length > 0 ? (
            <div className="history-list">
              {existingNotes.map((note, index) => {
                const noteHealth = PROJECT_HEALTH_OPTIONS.find((o) => o.value === note.health);
                const noteColor = noteHealth?.color || PROJECT_HEALTH_COLORS[note.health] || '#8A8F98';

                return (
                  <div key={`${note.createdAt}-${index}`} className="note-card">
                    <div className="note-header">
                      {healthDot(noteColor)}
                      <span className="note-label" style={{ color: noteColor }}>
                        {noteHealth?.label || note.health}
                      </span>
                      <span className="note-date">{note.date}</span>
                    </div>
                    <div className="note-author">{note.authorName || 'Unknown'}</div>
                    {note.statusNote && (
                      <div className="note-field">
                        <span className="note-field-label">Status: </span>
                        {note.statusNote}
                      </div>
                    )}
                    {note.actionNote && (
                      <div className="note-field">
                        <span className="note-field-label">Action: </span>
                        {note.actionNote}
                      </div>
                    )}
                    {note.followUpPersonName && (
                      <div className="note-field">
                        <span className="note-field-label">Follow-up: </span>
                        {note.followUpPersonName}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="history-empty">No notes yet</div>
          )}
        </div>
      </div>
    </ModalV2>
  );
};

export default ProjectHealthModal;
